import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import TimetableView from '../Timetable/TimetableView';
import './ClassDetailPage.css';

const ClassDetailPage = ({ classData, onBack }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(false);
  const [classStudents, setClassStudents] = useState([]);
  const [classNotices, setClassNotices] = useState([]);
  const [academicMatters, setAcademicMatters] = useState([]);
  const [timetable, setTimetable] = useState([]);

  useEffect(() => {
    if (classData) {
      fetchClassData();
    }
  }, [classData]);

  const fetchClassData = async () => {
    setLoading(true);
    try {
      // Fetch class students
      const { data: students } = await supabase
        .from('saas_class_members')
        .select('*')
        .eq('class_id', classData.class_id)
        .limit(50);

      if (students) {
        setClassStudents(students);
      }

      // Mock data for other sections (replace with actual API calls)
      setClassNotices([
        {
          id: 1,
          title: '下週考試安排',
          content: '下週三將進行期中考試，請同學們做好準備。',
          date: '2025-01-20',
          priority: 'high'
        },
        {
          id: 2,
          title: '班會通知',
          content: '本週五下午將舉行班會，討論下月活動安排。',
          date: '2025-01-18',
          priority: 'medium'
        }
      ]);

      setAcademicMatters([
        {
          id: 1,
          type: 'exam',
          title: '期中考試成績公佈',
          description: '期中考試成績已公佈，請同學們查看。',
          date: '2025-01-15'
        },
        {
          id: 2,
          type: 'homework',
          title: '數學作業繳交',
          description: '請於本週五前繳交數學作業。',
          date: '2025-01-17'
        }
      ]);

      setTimetable([
        {
          day: 'Monday',
          periods: [
            { time: '08:00-08:45', subject: 'Chinese', teacher: '王老師' },
            { time: '08:50-09:35', subject: 'English', teacher: '李老師' },
            { time: '09:40-10:25', subject: 'Mathematics', teacher: '張老師' },
            { time: '10:30-10:45', subject: 'Break', teacher: '' },
            { time: '10:45-11:30', subject: 'Science', teacher: '陳老師' },
            { time: '11:35-12:20', subject: 'History', teacher: '劉老師' }
          ]
        },
        {
          day: 'Tuesday',
          periods: [
            { time: '08:00-08:45', subject: 'English', teacher: '李老師' },
            { time: '08:50-09:35', subject: 'Mathematics', teacher: '張老師' },
            { time: '09:40-10:25', subject: 'Chinese', teacher: '王老師' },
            { time: '10:30-10:45', subject: 'Break', teacher: '' },
            { time: '10:45-11:30', subject: 'Geography', teacher: '黃老師' },
            { time: '11:35-12:20', subject: 'Physical Education', teacher: '林老師' }
          ]
        }
      ]);

    } catch (error) {
      console.error('Error fetching class data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return '#e74c3c';
      case 'medium': return '#f39c12';
      case 'low': return '#27ae60';
      default: return '#95a5a6';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'exam': return '📝';
      case 'homework': return '📚';
      case 'notice': return '📢';
      default: return '📋';
    }
  };

  if (!classData) {
    return (
      <div className="class-detail-error">
        <p>沒有班別資料</p>
        <button onClick={onBack}>返回</button>
      </div>
    );
  }

  return (
    <div className="class-detail-page">
      {/* Header */}
      <div className="class-detail-header">
        <button className="back-btn" onClick={onBack}>
          ← 返回課室管理
        </button>
        <div className="class-info">
          <h1>{classData.class_name} - 班別詳情</h1>
          <p className="class-id">{classData.class_id}</p>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="class-tabs">
        <button 
          className={`tab-btn ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          班別概覽
        </button>
        <button 
          className={`tab-btn ${activeTab === 'timetable' ? 'active' : ''}`}
          onClick={() => setActiveTab('timetable')}
        >
          課程時間表
        </button>
        <button 
          className={`tab-btn ${activeTab === 'students' ? 'active' : ''}`}
          onClick={() => setActiveTab('students')}
        >
          班別學生
        </button>
        <button 
          className={`tab-btn ${activeTab === 'notices' ? 'active' : ''}`}
          onClick={() => setActiveTab('notices')}
        >
          班別通告
        </button>
        <button 
          className={`tab-btn ${activeTab === 'academic' ? 'active' : ''}`}
          onClick={() => setActiveTab('academic')}
        >
          學術事務
        </button>
      </div>

      {/* Content Area */}
      <div className="class-content">
        {loading ? (
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>載入中...</p>
          </div>
        ) : (
          <>
            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div className="overview-section">
                <div className="overview-grid">
                  <div className="overview-card">
                    <h3>班主任</h3>
                    {classData.class_master ? (
                      <div className="teacher-detail">
                        <span className="teacher-name">{classData.class_master.name}</span>
                        <span className="teacher-dept">{classData.class_master.department}</span>
                        <span className="teacher-pos">{classData.class_master.position}</span>
                      </div>
                    ) : (
                      <span className="no-data">未安排</span>
                    )}
                  </div>

                  <div className="overview-card">
                    <h3>副班主任</h3>
                    {classData.class_vice_master ? (
                      <div className="teacher-detail">
                        <span className="teacher-name">{classData.class_vice_master.name}</span>
                        <span className="teacher-dept">{classData.class_vice_master.department}</span>
                        <span className="teacher-pos">{classData.class_vice_master.position}</span>
                      </div>
                    ) : (
                      <span className="no-data">未安排</span>
                    )}
                  </div>

                  <div className="overview-card">
                    <h3>主要科目</h3>
                    <div className="subjects-overview">
                      {classData.offered_subjects?.map((subject, index) => (
                        <span key={index} className="subject-item">{subject}</span>
                      ))}
                    </div>
                  </div>

                  <div className="overview-card">
                    <h3>學生人數</h3>
                    <span className="student-count">{classStudents.length}</span>
                    <p>名學生</p>
                  </div>
                </div>
              </div>
            )}

            {/* Timetable Tab */}
            {activeTab === 'timetable' && (
              <div className="timetable-section">
                <TimetableView
                  type="class"
                  entityId={classData.class_id}
                  entityName={classData.class_name}
                  showCloseButton={false}
                />
              </div>
            )}

            {/* Students Tab */}
            {activeTab === 'students' && (
              <div className="students-section">
                <h3>班別學生 ({classStudents.length})</h3>
                <div className="students-grid">
                  {classStudents.map((student, index) => (
                    <div key={index} className="student-card">
                      <div className="student-avatar">
                        {student.student_id?.charAt(0) || 'S'}
                      </div>
                      <div className="student-info">
                        <span className="student-id">{student.student_id}</span>
                        <span className="student-name">學生 {index + 1}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Notices Tab */}
            {activeTab === 'notices' && (
              <div className="notices-section">
                <h3>班別通告</h3>
                <div className="notices-list">
                  {classNotices.map((notice) => (
                    <div key={notice.id} className="notice-card">
                      <div className="notice-header">
                        <h4>{notice.title}</h4>
                        <span 
                          className="priority-badge"
                          style={{ backgroundColor: getPriorityColor(notice.priority) }}
                        >
                          {notice.priority === 'high' ? '高' : notice.priority === 'medium' ? '中' : '低'}
                        </span>
                      </div>
                      <p className="notice-content">{notice.content}</p>
                      <span className="notice-date">{notice.date}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Academic Tab */}
            {activeTab === 'academic' && (
              <div className="academic-section">
                <h3>學術事務</h3>
                <div className="academic-list">
                  {academicMatters.map((matter) => (
                    <div key={matter.id} className="academic-card">
                      <div className="academic-icon">
                        {getTypeIcon(matter.type)}
                      </div>
                      <div className="academic-content">
                        <h4>{matter.title}</h4>
                        <p>{matter.description}</p>
                        <span className="academic-date">{matter.date}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ClassDetailPage;
